import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const WEIGHT_LABELS = {
  contactability: "Contactability",
  seniority: "Seniority",
  company_fit: "Company Fit",
  engagement: "Engagement",
  recency: "Recency",
};

export default function ScoringSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get("/api/leads/scoring/settings", { withCredentials: true });
      setSettings(res.data);
    } catch (err) {
      toast.error("Error loading scoring settings");
    } finally {
      setLoading(false);
    }
  };

  const handleWeightChange = (key, value) => {
    const newWeights = { ...settings.weights, [key]: value[0] };
    const keys = Object.keys(WEIGHT_LABELS);
    const otherKeys = keys.filter(k => k !== key);
    const otherSum = otherKeys.reduce((s, k) => s + newWeights[k], 0);
    const remaining = 100 - value[0];
    if (otherSum !== remaining && otherKeys.length > 0) {
      const scale = otherSum > 0 ? remaining / otherSum : 0;
      otherKeys.forEach(k => { newWeights[k] = Math.round(newWeights[k] * scale); });
      const diff = 100 - Object.values(newWeights).reduce((a, b) => a + b, 0);
      if (diff !== 0) newWeights[otherKeys[otherKeys.length - 1]] += diff;
    }
    setSettings({ ...settings, weights: newWeights });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put("/api/leads/scoring/settings", settings, { withCredentials: true });
      toast.success("Scoring settings saved. Recalculating scores...");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await axios.post("/api/leads/scoring/recalculate", {}, { withCredentials: true });
      toast.success("Score recalculation complete");
    } catch (err) {
      toast.error("Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) return <Card><CardContent className="p-6"><Loader2 className="h-4 w-4 animate-spin" /></CardContent></Card>;
  if (!settings) return null;

  const weightSum = Object.values(settings.weights).reduce((a, b) => a + b, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          Lead Scoring
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            />
            <Label className="text-sm font-normal">{settings.enabled ? "Enabled" : "Disabled"}</Label>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Adjust how lead scores are weighted. Total must equal 100 (currently {weightSum}).
          </p>
          {Object.entries(WEIGHT_LABELS).map(([key, label]) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm">{label}</Label>
                <span className="text-sm font-mono w-8 text-right">{settings.weights[key]}</span>
              </div>
              <Slider
                value={[settings.weights[key]]}
                onValueChange={(v) => handleWeightChange(key, v)}
                min={0} max={50} step={5}
              />
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={saving || weightSum !== 100}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
          <Button variant="outline" onClick={handleRecalculate} disabled={recalculating}>
            {recalculating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Recalculate All Scores
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
